import { useEffect, useMemo, useState } from "react";

import { InnerPageChrome } from "@/components/layout/InnerPageChrome";
import { SITE_ROUTES } from "@/config/siteRoutes";
import { getVenueXPreviewPayload } from "@/data/previewContent";
import { useVorinAssets } from "@/pages/shared/useVorinAssets";
import { MINIMALISTIC_PORTFOLIO_STYLESHEETS } from "@/pages/shared/vorinDemoStyles";
import { getSiteLocale, localizeHref } from "@/i18n/locale";
import { localizeContent, uiText } from "@/i18n/translations";

import {
  DEFAULT_BLOG_JOURNAL,
  fetchBlogPosts,
  type BlogIndexResponse,
  type BlogJournalSettings,
  type BlogPostSummary,
} from "./blogApi";
import styles from "./BlogPage.module.scss";

export function BlogPage() {
  const locale = getSiteLocale();
  useVorinAssets({ extraStylesheets: MINIMALISTIC_PORTFOLIO_STYLESHEETS });
  const previewContent = getVenueXPreviewPayload<BlogIndexResponse>("blog-index");
  const [posts, setPosts] = useState<BlogPostSummary[]>(() => localizeContent(previewContent?.items || [], locale));
  const [isLoading, setIsLoading] = useState(!previewContent);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [journal, setJournal] = useState<BlogJournalSettings>(
    () => localizeContent(previewContent?.journal || DEFAULT_BLOG_JOURNAL, locale),
  );
  const content = journal.presentation;

  useEffect(() => {
    if (previewContent) return;

    const controller = new AbortController();
    fetchBlogPosts(controller.signal)
      .then((response) => {
        setPosts(response.items);
        setJournal(response.journal || localizeContent(DEFAULT_BLOG_JOURNAL, locale));
        setError("");
      })
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === "AbortError") return;
        setError(reason instanceof Error ? reason.message : uiText("The journal could not be loaded.", locale));
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [previewContent]);

  useEffect(() => {
    document.title = `${journal.seoTitle || journal.title} | VenueX`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", journal.seoDescription || journal.intro);
  }, [journal]);

  const categories = useMemo(
    () => Array.from(new Map(posts.map((post) => [post.categorySlug, post.category])).entries()),
    [posts],
  );
  const visiblePosts = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return posts.filter((post) => {
      const matchesCategory = activeCategory === "all" || post.categorySlug === activeCategory;
      const haystack = `${post.title} ${post.excerpt} ${post.category} ${post.tags.join(" ")}`.toLocaleLowerCase();
      return matchesCategory && (!normalizedQuery || haystack.includes(normalizedQuery));
    });
  }, [activeCategory, posts, query]);

  return (
    <InnerPageChrome currentPath={SITE_ROUTES.blog}>
      <article className={styles.page}>
        <section className={styles.hero}>
          <div className="container-fluid">
            <h1 className={styles.heroTitle}>{content.archive_title}</h1>
          </div>
        </section>

        <section className={styles.introSection}>
          <div className="container-fluid">
            <div className={styles.introRow}>
              <div className={styles.introBadge}>
                <span className={styles.star}>&#10038;</span>
                <div>
                  <span className={styles.kicker}>{content.archive_kicker}</span>
                  <p className={styles.kickerText}>{content.archive_kicker_copy}</p>
                </div>
              </div>

              <h2 className={styles.introHeading}>{content.archive_intro}</h2>
            </div>
          </div>
        </section>

        <section className={styles.gridSection}>
          <div className="container-fluid">
            <div className={styles.filterBar}>
              <div className={styles.categories} aria-label={content.filter_label}>
                <button className={activeCategory === "all" ? styles.filterActive : ""} onClick={() => setActiveCategory("all")} type="button">{content.all_label}</button>
                {categories.map(([slug, label]) => (
                  <button className={activeCategory === slug ? styles.filterActive : ""} key={slug} onClick={() => setActiveCategory(slug)} type="button">{label}</button>
                ))}
              </div>
              <label className={styles.searchField}>
                <span className="visually-hidden">{content.search_label}</span>
                <input onChange={(event) => setQuery(event.target.value)} placeholder={content.search_placeholder} type="search" value={query} />
                <span aria-hidden="true">↗</span>
              </label>
            </div>

            {error ? <div className={styles.statePanel} role="alert"><strong>{content.unavailable_title}</strong><span>{error} {content.retry_message}</span></div> : null}
            {isLoading ? <div className={styles.loadingGrid} aria-label={content.loading_label}><span></span><span></span><span></span><span></span></div> : null}
            <div className={styles.grid}>
              {visiblePosts.map((post) => (
                <a
                  key={post.slug}
                  className={`${styles.card} ${post.featured ? styles.cardFeatured : ""}`}
                  href={localizeHref(`${SITE_ROUTES.blog}${post.slug}/`, locale)}
                >
                  <div className={styles.cardImage}>
                    <img src={post.heroImage} alt={post.heroImageAlt} loading={post.featured ? "eager" : "lazy"} />
                    <div className={styles.overlay}></div>
                  </div>

                  <div className={styles.cardBody}>
                    <span className={styles.category}>{post.category}</span>
                    <span className={styles.date}>{post.date}</span>
                    <h3 className={styles.cardTitle}>{post.title}</h3>
                    <p className={styles.cardExcerpt}>{post.excerpt}</p>
                  </div>
                </a>
              ))}
            </div>
            {!isLoading && !error && visiblePosts.length === 0 ? <div className={styles.statePanel}><strong>{content.empty_title}</strong><span>{content.empty_message}</span></div> : null}
          </div>
        </section>
      </article>
    </InnerPageChrome>
  );
}
